import { DiscussionContextType, QUERY_KEYS } from "@cnx/common";
import { useQueryClient } from "@tanstack/react-query";
import { useChatAvailability } from "hooks/useChatAvailability";
import { useDiscussions } from "hooks/useDiscussions";
import { useEffect, useMemo } from "react";
import { useSocket } from "src/socket/useSocket";
import { useAuthStore } from "stores/authStore";
import { getQueryKeysForDiscussionContext } from "utils/chat";

const useInvalidateMessages = ({ isChatEnabled = false }) => {
  const socket = useSocket();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore(state => state.currentUserId);

  useEffect(() => {
    if (!socket || !currentUserId || !isChatEnabled) return;

    const invalidateQueries = ({
      context,
    }: { context?: DiscussionContextType } = {}) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DISCUSSIONS],
      });

      if (context) {
        getQueryKeysForDiscussionContext(context)?.forEach(queryKey => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
    };

    const invalidateMessageQueries = ({
      discussionId,
      senderId,
    }: {
      discussionId: string;
      senderId?: string;
    }) => {
      if (senderId === currentUserId) return;
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MESSAGES, discussionId],
      });
    };

    socket.on("invalidateDiscussionQueries", invalidateQueries);
    socket.on("invalidateMessageQueries", invalidateMessageQueries);

    return () => {
      socket.off("invalidateDiscussionQueries", invalidateQueries);
      socket.off("invalidateMessageQueries", invalidateMessageQueries);
    };
  }, [socket, currentUserId, isChatEnabled, queryClient]);
};

export const useUnreadMessagesCount = () => {
  const currentUserId = useAuthStore(state => state.currentUserId);

  const isChatEnabled = useChatAvailability();
  useInvalidateMessages({ isChatEnabled });

  const {
    discussions = [],
    isPending: discussionsIsLoading,
    isSuccess,
  } = useDiscussions({ enabled: isChatEnabled });

  const unreadMessagesCount = useMemo(() => {
    if (!isChatEnabled) return 0;
    if (discussionsIsLoading || !isSuccess) return 0;

    return discussions.reduce((acc, discussion) => {
      if (
        discussion.unreadMessageCount &&
        discussion.lastMessageCreatedBy !== currentUserId
      ) {
        return acc + discussion.unreadMessageCount;
      }
      return acc;
    }, 0);
  }, [
    isChatEnabled,
    discussions,
    discussionsIsLoading,
    isSuccess,
    currentUserId,
  ]);

  return { unreadMessagesCount, isChatEnabled };
};
